// services/cardService.ts
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3944";

// Interfaces (mêmes que ci-dessus)
export interface CardTransaction {
  id: number;
  merchant_code: string;
  transaction_uuid: string;
  amount: number;
  currency: string;
  merchant_reference: string;
  status: 'PENDING' | 'SUCCESS' | 'FAILED' | 'CANCELLED';
  description?: string;
  bill_to_email?: string;
  bill_to_forename?: string;
  bill_to_surname?: string;
  created_at: string;
  updated_at: string;
  total_commission_amount: number;
  merchant_amount: number;
  decision?: string;
  reason_code?: string;
  reconciliation_reference_number?: string;
}

export interface CardTransactionsResponse {
  transactions: CardTransaction[];
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface Wallet {
  id: number;
  merchant_code: string;
  virtual_card_number: string;
  currency: string;
  balance: number;
  created_at: string;
  updated_at: string;
}

export interface CardWallet {
  id: number;
  merchant_code: string;
  virtual_card_number: string;
  currency: string;
  balance: number;
  created_at: string;
  updated_at: string;
  mode: 'test' | 'production';
  environment?: 'development' | 'production';
}

export interface CardWalletBalanceResponse {
  wallets: CardWallet[];
  summary: {
    live: { usd: number; cdf: number };
    test: { usd: number; cdf: number };
    // Legacy (kept for backward compatibility)
    total_balance_usd: number;
    total_balance_cdf: number;
    last_updated: string;
  };
}

export interface WalletTransaction {
  id: number;
  wallet_id: number;
  transaction_type: 'DEPOSIT' | 'WITHDRAWAL' | 'TRANSFER';
  amount: number;
  currency: string;
  reference: string;
  description?: string;
  status: 'COMPLETED' | 'PENDING' | 'FAILED';
  previous_balance: number;
  new_balance: number;
  created_at: string;
}

export interface WalletTransactionsResponse {
  transactions: WalletTransaction[];
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

// Fonction utilitaire pour obtenir les headers avec token
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
};

export const cardService = {
  // Card Transactions
  async getCardTransactions(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<CardTransactionsResponse> {
    const response = await axios.get(
      `${API_URL}/merchants/card-transactions`,
      {
        params,
        ...getAuthHeaders()
      }
    );
    return response.data;
  },

  async exportCardTransactions(params?: {
    search?: string;
    status?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<Blob> {
    const response = await axios.get(
      `${API_URL}/merchants/card-transactions/export`,
      {
        params,
        responseType: 'blob',
        ...getAuthHeaders()
      }
    );
    return response.data;
  },

  // Wallets
  async getWallets(): Promise<Wallet[]> {
    const response = await axios.get(
      `${API_URL}/merchants/wallets`,
      getAuthHeaders()
    );
    return response.data;
  },

  async getWalletTransactions(params?: {
    page?: number;
    limit?: number;
    wallet_id?: string;
    search?: string;
    start_date?: string;
    end_date?: string;
    transaction_type?: string;
  }): Promise<WalletTransactionsResponse> {
    const response = await axios.get(
      `${API_URL}/merchants/wallet-transactions`,
      {
        params,
        ...getAuthHeaders()
      }
    );
    return response.data;
  },

  // Récupérer les soldes des cartes virtuelles (CORRECTION : utiliser axios.get au lieu de api.get)
  async getWalletBalances(): Promise<CardWalletBalanceResponse> {
    try {
      const response = await axios.get(
        `${API_URL}/merchants/card-wallets/balances`,
        getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching wallet balances:', error);
      throw error;
    }
  },

  // Formater le numéro de carte
  formatCardNumber(cardNumber: string): string {
    return cardNumber.replace(/(\d{4})/g, '$1 ').trim();
  },

  // Masquer le numéro de carte (afficher seulement les 4 derniers chiffres)
  maskCardNumber(cardNumber: string): string {
    return `**** **** **** ${cardNumber.slice(-4)}`;
  },

  // Copier dans le presse-papier
  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text);
  },

  // Télécharger un export
  async downloadExport(blob: Blob, filename: string = 'transactions-export.csv') {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  formatFilename(baseName: string, extension: string = 'csv'): string {
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-');
    return `${baseName}-${dateStr}-${timeStr}.${extension}`;
  }
};